/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Anchor, Card, Pagination } from '@nexigendigital/nxui'
import { billingAPI } from 'api/billing'
import { setInvoice } from 'store/slices/billingSlice'
import { Transaction } from 'models/billing'
import { aud, parseISO } from 'helpers/utils'

import { ViewTransactionLightbox } from 'containers/billing/lightbox/viewTransactionLightbox'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function TransactionsContainer() {
    const dispatch = useDispatch()
    const [currentPage, setCurrentPage] = useState(1)
    const { isLoading, isSuccess, data } = billingAPI.endpoints.transactions.useQuery(currentPage)
    const [transactions, setTransactions] = useState([])
    const [lightboxStatus, setLightboxStatus] = useState(false)
    const columns = useMemo(() => [
        {
            Header: 'Date',
            accessor: 'date'
        },
        {
            Header: 'Reference',
            accessor: 'reference'
        },
        {
            Header: 'Invoice',
            accessor: 'invoice'
        },
        {
            Header: 'Refund Method',
            accessor: 'refundMethod'
        },
        {
            Header: 'Amount',
            accessor: 'amount'
        }
    ], [])

    useEffect(() => {
        if (isSuccess) {
            setTransactions(data.data.map(({ amount, refund_method, invoice, reference, transaction_date }: Transaction) => ({
                reference,
                date: parseISO(transaction_date).setLocale('en-AU').toLocaleString(),
                invoice: invoice
                    ? <Anchor color={'primary'} onClick={() => dispatch(setInvoice(invoice.id))}>{invoice.id}</Anchor>
                    : '-',
                refundMethod: refund_method,
                amount: <BlackText>{aud(amount)}</BlackText>
            })))
        }
    }, [data, dispatch, isSuccess])

    return (
        <>
            <Transactions.Card
                table={{
                    conditions: {
                        loading: isLoading && 'Loading Transactions...',
                        search: false,
                        pagination: false
                    },
                    render: {
                        columns: () => columns,
                        data: () => transactions
                    }
                }}
                footer={(
                    <Pagination
                        currentPage={data?.meta.current_page || 1}
                        totalCount={data?.meta.total || 1}
                        pageSize={data?.meta.per_page || 15}
                        onPageChange={(page: number) => setCurrentPage(page)}
                    />
                )}
            />
            <ViewTransactionLightbox status={lightboxStatus} close={() => setLightboxStatus(false)} />
        </>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const Transactions = {
    Card: styled(Card)`
        width: 100%;
        margin-bottom: 0;
        border-radius: 0 0 16px 16px;

        > header {
            border-radius: 0;
        }
    `
}
const BlackText = styled.span`
    color: ${props => props.theme['black--100']};
`

export { TransactionsContainer as Transactions }
